import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { AccountApi } from '../data/api.data';
import { LocalStoreEnums } from '../enums/store.enum';
import { RoleEnums } from '../enums/user.enum';
import {
  AdminAccountI,
  AuthI,
  Permission,
  PermissionActions,
  PermissionsScopes,
  TokenDataI,
} from '../interfaces/account.interface';
import { CookieService } from './cookie.service';
import { RequestService } from './request.service';
import { MenuI, MenuItems } from '../data/menu';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(
    private router: Router,
    private reqS: RequestService,
    private cookieS: CookieService,
    private jwtHelper: JwtHelperService
  ) {}

  isAthourized(allowedRoles: string[]): any {
    // check if the list of allowedusertpes for aroute is empty,
    // if empty, authorize the user to access the page
    if (allowedRoles == null || allowedRoles.length === 0) {
      return true;
    }
    const role = this.userRole;
    if (role) {
      return allowedRoles.includes(role);
    }
    return false;
  }

  get isLoggedIn(): boolean {
    const tokenDuration = this.tokenDuration;
    return tokenDuration > 0;
  }

  get tokenDuration(): number {
    const tkn = this.getToken;

    if (!tkn) {
      return 0;
    }
    const token = this.jwtHelper.decodeToken(tkn) as TokenDataI;
    const duration = token.exp - token.iat;
    return duration;
  }

  get getToken(): string {
    const tkn = this.cookieS.getCookie(LocalStoreEnums.TOKEN);
    return tkn ? tkn : '';
  }

  get userRole(): RoleEnums {
    const decodedToken = this.jwtHelper.decodeToken(
      this.getToken
    ) as TokenDataI;
    return decodedToken.role;
  }

  get isSuperAdmin() {
    return this.userRole === RoleEnums.SUPER_ADMIN;
  }

  get isBusinessAdmin() {
    return (
      this.userRole === RoleEnums.STAFF || this.userRole === RoleEnums.ADMIN
    );
  }

  get userPermissions(): Permission {
    const decodedToken = this.jwtHelper.decodeToken(
      this.getToken
    ) as TokenDataI;

    return decodedToken?.permissions;
  }

  get userProfile(): AdminAccountI {
    const user = this.cookieS.getCookie(LocalStoreEnums.USER);
    return JSON.parse(user);
  }

  get authMenu(): MenuI[] {
    const permissions = this.userPermissions;

    if (!permissions) {
      return [];
    }
    const filteredMenu = MenuItems.filter((menuItem) => {
      // If the menu item does not have a `scope` field, it passes.
      if (!menuItem.scope) {
        return true;
      }

      if (
        menuItem?.allowedRoles &&
        !menuItem?.allowedRoles?.includes(this.userRole)
      ) {
        return false;
      }

      // Check if the scope exists in the permissions object and includes 'read'
      const scopePermissions = permissions[menuItem.scope];
      return scopePermissions && scopePermissions.includes('read');
    });
    return filteredMenu;
  }

  isPermissionAlllowed(scope: PermissionsScopes, action: PermissionActions) {
    return true; // Allow everybody
    const permissions = this.userPermissions;
    const scopePermissions = permissions[scope];
    if (!scopePermissions) {
      return false;
    }
    return scopePermissions.includes(action);
  }

  canWrite(scope: PermissionsScopes) {
    return this.isPermissionAlllowed(scope, 'write');
  }

  canRead(scope: PermissionsScopes) {
    return this.isPermissionAlllowed(scope, 'read');
  }

  canDelete(scope: PermissionsScopes) {
    return this.isPermissionAlllowed(scope, 'delete');
  }

  canUpdate(scope: PermissionsScopes) {
    return this.isPermissionAlllowed(scope, 'update');
  }

  // set token to cookie
  public setToken(token: string) {
    this.cookieS.setCookie({
      name: LocalStoreEnums.TOKEN,
      value: token,
    });
  }

  public setUser(user: AdminAccountI) {
    this.cookieS.setCookie({
      name: LocalStoreEnums.USER,
      value: user,
    });
  }

  public startLogin(data: {
    email: string;
    password: string;
  }): Observable<{ data: { minutes_left: number; expires_at: string } }> {
    return this.reqS.post(AccountApi.startLogin, data);
  }

  public completeLogin(data: {
    email: string;
    password: string;
  }): Observable<{ data: AuthI }> {
    return this.reqS.post(AccountApi.completeLogin, data);
  }

  public dashboardData(): Observable<{
    data: {
      total_users: number;
      students: number;
      standard: number;
      active: number;
      total_paid: number;
      recent_regisrations: any[];
    };
  }> {
    return this.reqS.get(AccountApi.dashboard);
  }

  public logout() {
    this.cookieS.deleteCookie(LocalStoreEnums.USER);
    this.cookieS.deleteCookie(LocalStoreEnums.TOKEN);
    this.router.navigate(['/']);
  }

  public createAccount(data: { phoneNumber: string }): Observable<AuthI> {
    return this.reqS.post(AccountApi.signup, data);
  }
  public updateProfile(data: object) {
    return this.reqS.post<{ message: string; data: AdminAccountI }>(
      AccountApi.updateProfile,
      data
    );
  }
  public changePassword(data: object) {
    return this.reqS.post<{ message: string; data: AdminAccountI }>(
      AccountApi.changePassword,
      data
    );
  }

  public resetPassword(data: object) {
    return this.reqS.post<{ message: string; status: string }>(
      AccountApi.resetPassword,
      data
    );
  }
  public requestPasswordReset(data: { phoneNumber: string }) {
    return this.reqS.post<{ message: string; data: AdminAccountI }>(
      AccountApi.requestPasswordReset,
      data
    );
  }
}
